<template>
    <div>
        <h3 class="block w-full border-b border-black border-opacity-5 text-lg lg:text-xl font-Inter-Bold pb-4 mb-6">
            {{ $t('checkout.cart.title') }}
        </h3>
        <transition name="fade" mode="out-in">
            <div v-if="cartItems.length" class="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mb-8 lg:mb-14">
                <div
                    v-for="(item, i) in cartItems"
                    :key="`selected-product-${i}`"
                    :class="cartItems.length === 1 ? 'col-span-2 lg:col-span-1' : 'col-span-1'"
                >
                    <CartProductCard
                        :product="item"
                        :quantity="item.quantity"
                        :is-large="cartItems.length === 1"
                        :remove-cb="getProducts"
                        :update-cb="getCartTotal"
                    />
                </div>
            </div>

            <div v-else class="text-center pt-32 pb-32 my-8 rounded-2xl flex justify-center flex-col items-center">
                <div class="sad-emoji mb-4"></div>
                <div class="flex flex-col items-center">
                    <span class="text-xl block mb-2 font-Inter-Bold">{{ $t('checkout.cart.empty_title') }}</span>
                </div>
                <div class="flex flex-col items-center">
                    <span class="text-sm font-Inter-Regular block"
                        >{{ $t('checkout.cart.empty_description') }}</span
                    >
                </div>
                <PButton :label="$t('checkout.cart.browse_gallery')" class="mt-8" :to="localePath('products')" />
            </div>
        </transition>
        <div v-if="cartItems.length" class="flex items-center flex-wrap flex-col lg:flex-row">
            <div
                v-if="$device.isMobile && $store.state.checkout.currentCheckoutStep === 1"
                class="lg:px-10 mt-4 lg:mt-0 w-full mb-4 block lg:hidden"
            >
                <span class="text-base lg:text-lg font-Inter-Bold">{{ $t('checkout.total') }}</span>
                <transition name="fade-in-value" mode="out-in">
                    <div
                        :key="$formatCurrency(cartTotal)"
                        class="text-xl lg:text-3xl text-black block font-Inter-Semibold mt-1"
                    >
                        {{ $formatCurrency(parseInt(shippingPrice ? cartTotal + shippingPrice : cartTotal)) }}
                    </div>
                </transition>
                <div
                    v-if="$store.state.checkout_details.selected_shipping_method"
                    class="flex flex-col gap-y-1 mt-3 mb-2"
                >
                    <h5 class="flex mb-1 text-sm">
                        <span class="w-6/12 font-Inter-Bold">{{ $t('checkout.shipping_fee') }}</span>
                        <span class="w-6/12 lg:text-left text-right">{{ $formatCurrency(shippingPrice) }}</span>
                    </h5>
                    <h5 class="flex mb-1 text-sm">
                        <span class="w-6/12 font-Inter-Bold">{{ $t('checkout.products_price') }}</span>
                        <span class="w-6/12 lg:text-left text-right">{{ $formatCurrency(parseInt(cartTotal)) }}</span>
                    </h5>
                </div>
            </div>
            <div class="w-full lg:w-3/5 lg:pr-8 relative">
                <div class="flex items-center">
                    <ValidationObserver ref="form" v-slot="{ errors }" slim tag="div" class="w-full lg:w-auto">
                        <form
                            novalidate
                            class="flex flex-col lg:flex-row lg:items-center gap-x-4 w-full justify-between"
                            @submit.prevent="redeemCoupon"
                        >
                            <div class="w-full lg:w-8/12">
                                <ValidationProvider vid="error" tag="div">
                                    <Input
                                        v-model="couponCode"
                                        :label="$t('checkout.coupon.label')"
                                        :placeholder="$t('checkout.coupon.placeholder')"
                                    />
                                </ValidationProvider>
                            </div>
                            <div class="w-full lg:w-4/12">
                                <PButton
                                    type="submit"
                                    appearance="black"
                                    :label="$t('checkout.coupon.apply')"
                                    :has-preloader="isLoading"
                                    class="w-full lg:w-auto mt-3 lg:mt-[1.9rem] lg:min-w-[9rem]"
                                />
                            </div>
                        </form>
                        <InputError v-if="isErrorVisible" class="mt-1" :errors="errors.error" />
                    </ValidationObserver>
                </div>

                <transition name="fade">
                    <div
                        v-if="isCouponFeedbackMessageVisible"
                        class="absolute w-full h-full bg-white left-0 top-0 z-1 flex items-center gap-x-4"
                    >
                        <i class="ri-checkbox-circle-fill text-3xl text-green"></i>
                        <div class="flex flex-col">
                            <span class="block font-Inter-Bold text-lg mb-2">{{ $t('checkout.coupon.success_title') }}</span>
                            <span class="block font-Inter-Regular text-sm"
                                >{{ $t('checkout.coupon.success_description') }}</span
                            >
                        </div>
                    </div>
                </transition>
            </div>

            <div class="w-full lg:w-2/5 text-right">
                <PButton
                    :to="localePath('/rendeles/szemelyes-adatok')"
                    :label="$t('checkout.cart.proceed_to_order')"
                    appearance="yellow"
                    class="w-full lg:w-auto mt-[1.9rem]"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import CartProductCard from '~/components/Cards/CartProductCard/CartProductCard.vue';

export default {
    name: 'Cart',
    components: { CartProductCard },
    layout: 'checkout',
    async asyncData({ $axios, $cookies, store, error }) {
        let cartItems = [];
        let cartTotal = 0;

        try {
            await $axios.get(`/cart/${$cookies.get('hsession')}`).then((res) => {
                if (res.data.success !== true) return;

                cartTotal = parseInt(res.data.sum);
                cartItems = res.data.products;
                store.commit('checkout/setCartItemsCount', cartItems.length);
            });
        } catch (err) {
            console.log(err);
        }
        return { cartItems, cartTotal };
    },
    data() {
        return {
            couponCode: null,
            quantity: 1,
            isErrorVisible: false,
            isLoading: false,
            isCouponFeedbackMessageVisible: false,
            isDeleting: false,
        };
    },
    computed: {
        shippingPrice() {
            return this.$store.state.shipping_methods.find(
                (method) => method.id === this.$store.state.checkout_details.selected_shipping_method
            )?.cost;
        },
    },
    watch: {
        cartItems(value) {
            this.emptyShippingMethod();
        },
    },
    methods: {
        ...mapActions(['emptyShippingMethod']),
        updateQuantity({ id, quantity }) {
            const item = this.cartItems.find((item) => item.id === id);
            if (item) {
                item.quantity = quantity;
                this.getCartTotal();
            }
        },
        getProducts() {
            this.$axios.get(`/cart/${this.$cookies.get('hsession')}`).then((res) => {
                this.cartItems = res.data.products;
                this.$store.commit('checkout/setCartItemsCount', res.data.products.length);
                this.$store.commit('checkout/setCartTotal', res.data.sum);
            });
        },
        getCartTotal() {
            this.$axios.get(`/cart/${this.$cookies.get('hsession')}`).then((res) => {
                this.$store.commit('checkout/setCartItemsCount', res.data.products.length);
                this.$store.commit('checkout/setCartTotal', res.data.sum);
            });
        },
        async redeemCoupon() {
            this.isErrorVisible = true;
            this.isLoading = true;

            try {
                await this.$axios
                    .$post('/cart-coupon', {
                        couponCode: this.couponCode,
                        sessionId: this.$cookies.get('hsession'),
                    })
                    .then((res) => {
                        this.isLoading = false;
                        if (res.success === true) {
                            this.$axios.get(`/cart/${this.$cookies.get('hsession')}`).then((res) => {
                                this.cartItems = res.data.products;
                                this.$store.commit('checkout/setCartItemsCount', res.data.products.length);
                                this.$store.commit('checkout/setCartTotal', res.data.sum);
                            });
                            this.isCouponFeedbackMessageVisible = true;
                        } else {
                            this.$refs.form.setErrors({ error: res.error });
                        }
                    });
            } catch (err) {
                console.log(err);
            }
        },
    },
};
</script>
